/**
 * Traffic Light Menu Component
 * A reusable overflow ("kebab") menu rendered as a vertical or horizontal three-dot trigger
 * that opens a dropdown of actions. Safe to drop inside clickable table rows.
 * @packageDocumentation
 */

import { Fragment } from 'react';
import type { JSX, ReactNode } from 'react';
import { Ellipsis, EllipsisVertical } from 'lucide-react';

import { cn } from '@/lib/utils';

import { Button } from './button';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './dropdown-menu';

/**
 * A single action item in a TrafficLightMenu.
 */
export interface TrafficLightMenuItem {
  /** Unique key for the item. */
  key: string;
  /** The visible label text. */
  label: string;
  /** Optional leading icon. */
  icon?: ReactNode;
  /** Whether the item is disabled. */
  disabled?: boolean;
  /** Action invoked when the item is selected (deferred to the next tick after the menu closes). */
  onSelect: () => void;
  /** Whether the item renders with destructive (red) styling. */
  destructive?: boolean;
  /** Whether to render a separator above this item. */
  separatorBefore?: boolean;
}

/**
 * Props for the TrafficLightMenu component.
 */
export interface TrafficLightMenuProps {
  /** Accessible label for the trigger button. */
  label: string;
  /** Additional CSS classes for the trigger button. */
  triggerClassName?: string;
  /** The action items to render in the menu. */
  items: TrafficLightMenuItem[];
  /** Alignment of the menu content relative to the trigger. Defaults to 'end'. */
  align?: 'start' | 'center' | 'end';
  /** Orientation of the three-dot trigger icon. Defaults to 'vertical'. */
  orientation?: 'vertical' | 'horizontal';
}

/**
 * Reusable overflow ("traffic light" / kebab) menu.
 *
 * Renders a three-dot icon button that opens a dropdown of actions. Designed to be placed inside
 * clickable rows: it stops click propagation on both the trigger and the menu content (React
 * synthetic events bubble through portals along the React tree, which would otherwise trigger the
 * row's onClick), and it defers each item's action to the next tick so actions that open a dialog
 * do not race the menu's focus restoration.
 *
 * @param { TrafficLightMenuProps } root0 The component props.
 * @param { TrafficLightMenuItem[] } root0.items The action items to render.
 * @param { string } root0.label Accessible label for the trigger button.
 * @param { string } root0.align Alignment of the menu content. Defaults to 'end'.
 * @param { string } root0.orientation Orientation of the trigger icon. Defaults to 'vertical'.
 * @param { string } root0.triggerClassName Additional CSS classes for the trigger button.
 * @returns { JSX.Element } The rendered menu element.
 */
export const TrafficLightMenu = ({
  items,
  label,
  align = 'end',
  triggerClassName,
  orientation = 'vertical',
}: TrafficLightMenuProps): JSX.Element => {
  const Icon = orientation === 'horizontal' ? Ellipsis : EllipsisVertical;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="icon-xs"
          variant="ghost"
          aria-label={label}
          onClick={(e) => e.stopPropagation()}
          className={cn('text-muted-foreground hover:text-foreground', triggerClassName)}
        >
          <Icon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} onClick={(e) => e.stopPropagation()}>
        {items.map((item) => (
          <Fragment key={item.key}>
            {item.separatorBefore && <DropdownMenuSeparator />}
            <DropdownMenuItem
              disabled={item.disabled}
              onSelect={() => globalThis.setTimeout(item.onSelect, 0)}
              className={cn(item.destructive && 'text-destructive focus:bg-destructive/10 focus:text-destructive')}
            >
              {item.icon}
              {item.label}
            </DropdownMenuItem>
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
